import React, { useState } from "react";

import { ButtonLoading, ButtonStandard, ModalBase, Spinner, Text, toast } from "@performyard/py-components";

import { EmployeeQueryBuilder } from "../../../../components/EmployeeQueryBuilder";
import type { EmployeeQuery } from "../../../../components/EmployeeQueryBuilder/types";

import { useEmployeeFields, useUpdateReviewCycle } from "../../../../api/hooks";

type EditAutomationRulesModalProps = {
	reviewCycleId: string;
	initialQuery: EmployeeQuery | null;
	isOpen: boolean;
	onClose: () => void;
};

export const EditAutomationRulesModal = ({
	reviewCycleId,
	initialQuery,
	isOpen,
	onClose,
}: EditAutomationRulesModalProps) => {
	const [query, setQuery] = useState<EmployeeQuery | null>(initialQuery);
	const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
	if (isOpen !== prevIsOpen) {
		setPrevIsOpen(isOpen);
		if (isOpen) {
			setQuery(initialQuery);
		}
	}

	const { employeeFields, isLoading } = useEmployeeFields();

	const { mutate: updateReviewCycle, isPending } = useUpdateReviewCycle({
		onSuccess: () => {
			toast.success("Automation rules updated successfully");
			onClose();
		},
	});

	const hasConditions = !!query && query.and.length > 0;

	const handleSave = () => {
		if (!query) return;
		updateReviewCycle({ reviewCycleId, data: { autoflow: { userQuery: query } } });
	};

	return (
		<ModalBase isOpen={isOpen} onClose={onClose} dangerouslySetClassName="w-[784px] max-w-[90vw]">
			<div className="flex flex-col gap-6 px-6 pb-6">
				<Text size="sub-heading" weight="semi-bold" dangerouslySetClassName="text-center">
					Edit Cycle Automation Rules
				</Text>
				{isLoading ? (
					<div className="flex min-h-[472px] items-center justify-center">
						<Spinner size={32} />
					</div>
				) : (
					<div className="min-h-[472px]">
						<EmployeeQueryBuilder
							employeeFields={employeeFields}
							value={query}
							onChange={setQuery}
							enableAnniversary={true}
							fieldSelectPlaceholder="Select a field"
						/>
					</div>
				)}
				<div className="flex w-full flex-wrap justify-center gap-3">
					<ButtonStandard
						size="large"
						variant="outline"
						color="neutral"
						dangerouslySetClassName="w-auto px-[16px]"
						onClick={onClose}
					>
						Cancel
					</ButtonStandard>
					<ButtonLoading
						size="large"
						color="primary"
						dangerouslySetClassName="w-auto px-[16px]"
						loading={isPending}
						disabled={!hasConditions}
						onClick={handleSave}
					>
						Save Changes
					</ButtonLoading>
				</div>
			</div>
		</ModalBase>
	);
};
